import VideoMeetingPage from '@modules/zoom/components/VideoMeeting/VideoMeeting';
import VideoWaitingRoomPage from '@modules/zoom/components/VideoWaitingRoom/VideoWaitingRoom';
import { ZoomContext } from '@modules/zoom/contexts/ZoomContext';
import ZoomVideo, { Stream } from '@zoom/videosdk';
import { useGetMeetingDetails, useGetVideoSettings } from 'apps/agora/src/actions';
import useToast from 'apps/agora/src/hooks/useToast';
import { useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useMeetingWebSocket from './useMeetingWebsocket';

const ERROR_LOG_WINDOW_MS = 60_000;
const MAX_ERROR_LOGS_PER_KEY_PER_WINDOW = 10;
const MAX_ERROR_LOGS_PER_SESSION = 300;

const describeObjectError = (error: Record<string, unknown>) => {
  const indexedKeys = Object.keys(error).filter((key) => /^\d+$/.test(key));
  if (!indexedKeys.length) return JSON.stringify(error, null, 2);

  const { type, errorCode, reason } = error;
  const message = indexedKeys
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => error[key])
    .join('');

  return JSON.stringify({ type, errorCode, reason, message }, null, 2);
};

const VideoMeeting = () => {
  const [zoomClient] = useState(() => ZoomVideo.createClient());
  const [isWaitingRoom, setIsWaitingRoom] = useState(true);
  const { meetingId } = useParams<{ meetingId: string }>();
  const [activeCamera, setActiveCamera] = useState<string | undefined>();
  const [activeMicrophone, setActiveMicrophone] = useState<string | undefined>();
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isMeetingLoading, setIsMeetingLoading] = useState(false);
  const [canJoinMeeting, setCanJoinMeeting] = useState(false);
  const [isZoomClientReady, setIsZoomClientReady] = useState(false);
  const [stream, setStream] = useState<typeof Stream | undefined>();

  const history = useHistory();

  const toast = useToast();

  const { data: meetingDetails } = useGetMeetingDetails(meetingId, {
    onError: (err: any) => {
      history.replace('/home');
      toast.error(
        err?.response?.data?.error?.message ?? err?.message ?? 'Meeting is not available.'
      );
    },
  });

  const { data: videoSettings } = useGetVideoSettings();

  const { webSocket, isSocketConnected, closeWebsocketConnection, sendRoomEvent } =
    useMeetingWebSocket();

  const errorLogBudgetRef = useRef(new Map<string, { count: number; windowStart: number }>());
  const errorLogTotalRef = useRef(0);

  const hasErrorLogBudget = (key: string) => {
    if (errorLogTotalRef.current >= MAX_ERROR_LOGS_PER_SESSION) return false;

    const now = Date.now();
    const entry = errorLogBudgetRef.current.get(key);

    if (entry && now - entry.windowStart < ERROR_LOG_WINDOW_MS) {
      if (entry.count >= MAX_ERROR_LOGS_PER_KEY_PER_WINDOW) return false;
      entry.count += 1;
    } else {
      errorLogBudgetRef.current.set(key, { count: 1, windowStart: now });
    }

    errorLogTotalRef.current += 1;
    return true;
  };

  const logError = (error: any) => {
    try {
      if (!webSocket || !zoomClient) return;

      let errorType = 'Unknown';
      let errorMessage = 'An unknown error occurred';
      let stackTrace = 'No stack trace available';

      if (error instanceof Error) {
        errorType = error.name || 'Error';
        errorMessage = error.message || 'An unknown error occurred';
        stackTrace = error.stack || 'No stack trace available';
      } else if (typeof error === 'string') {
        errorType = 'StringError';
        errorMessage = error;
      } else if (typeof error === 'object' && error !== null) {
        errorType = error.constructor?.name || 'ObjectError';
        errorMessage = describeObjectError(error);
      } else {
        errorType = typeof error;
        errorMessage = String(error);
      }

      if (!hasErrorLogBudget(`${errorType}:${errorMessage.slice(0, 120)}`)) return;

      // Collect diagnostic information
      const diagnostics = {
        browser: {
          userAgent: window.navigator.userAgent,
          platform: window.navigator.platform,
          language: window.navigator.language,
        },
        devices: {
          activeCamera,
          activeMicrophone,
        },
        mediaState: {
          isCameraActive,
          isMicrophoneActive,
        },
        sessionInfo: {
          userId: zoomClient?.getSessionInfo()?.userId,
          sessionId: zoomClient?.getSessionInfo()?.sessionId,
        },
      };

      webSocket.send(
        JSON.stringify({
          type: 'error-log',
          meetingId,
          username: zoomClient?.getSessionInfo()?.userName ?? 'Unknown User',
          errorType,
          errorMessage,
          stackTrace,
          diagnostics,
          timestamp: new Date().toISOString(),
        })
      );
    } catch (loggingError) {
      console.error('Error logging error:', loggingError);
    }
  };

  const contextValue = {
    stream,
    zoomClient,
    videoSettings: videoSettings || {},
    meetingDetails,
    activeCamera,
    activeMicrophone,
    isMicrophoneActive,
    isCameraActive,
    isMeetingLoading,
    canJoinMeeting,
    isZoomClientReady,
    webSocket,
    isSocketConnected,
    setActiveCamera,
    setActiveMicrophone,
    setIsMicrophoneActive,
    setIsCameraActive,
    setIsMeetingLoading,
    setCanJoinMeeting,
    setIsZoomClientReady,
    setStream,
    closeWebsocketConnection,
    sendRoomEvent,
    logError,
  };

  return (
    <ZoomContext.Provider value={contextValue}>
      {isWaitingRoom ? (
        <VideoWaitingRoomPage setIsWaitingRoom={setIsWaitingRoom} />
      ) : (
        <VideoMeetingPage />
      )}
    </ZoomContext.Provider>
  );
};

export default VideoMeeting;
